import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { projectsGrouped, companyInfo } from '../mock';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Find current project
  const currentIndex = projectsGrouped.findIndex(p => p.slug === slug);
  const project = projectsGrouped[currentIndex];
  
  // Get previous and next projects
  const prevProject = currentIndex > 0 ? projectsGrouped[currentIndex - 1] : null;
  const nextProject = currentIndex < projectsGrouped.length - 1 ? projectsGrouped[currentIndex + 1] : null;
  
  useEffect(() => {
    if (!project) {
      navigate('/projekter', { replace: true });
      return;
    }
    
    // Set page-specific meta tags
    const pageTitle = project.seoTitle || (project.isPublic 
      ? `${project.title} | Fryd! Tømrer & Snedker`
      : `Privat projekt | Fryd! Tømrer & Snedker`);
    document.title = pageTitle;
    
    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      const desc = project.seoDescription || (project.isPublic
        ? `Se vores arbejde på ${project.title}. Udført af Fryd! - tømrer og snedkerfirma med fokus på kvalitetshåndværk i træ.`
        : `Privat projekt udført af Fryd! - tømrer og snedkerfirma med fokus på kvalitetshåndværk i træ.`);
      metaDescription.setAttribute('content', desc);
    }
    
    // Robots meta for private projects
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', project.isPublic ? 'index, follow' : 'noindex, follow');
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://ffryd.dk/projekter/${slug}`);
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // Cleanup robots meta on unmount
    return () => {
      if (robotsMeta && !project.isPublic) {
        robotsMeta.setAttribute('content', 'index, follow');
      }
    };
  }, [project, slug, navigate]);
  
  if (!project) {
    return null;
  }
  
  // Split description into paragraphs
  const descriptionParagraphs = project.description ? project.description.split('\n\n') : [];
  
  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="pt-32 pb-20 px-8 md:px-16">
        <div className="max-w-7xl mx-auto">
          
          {/* Back navigation */}
          <Link 
            to="/projekter"
            className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors mb-12 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
            <span className="text-sm tracking-wide">Tilbage til projekter</span>
          </Link>
          
          {/* Project title */}
          <h1 className="text-4xl md:text-6xl font-light text-gray-900 mb-16">
            {project.title}
          </h1>
          
          {/* First image */}
          {project.images[0] && (
            <div className="w-full mb-12">
              <img
                src={project.images[0]}
                alt={`${project.title} - billede 1`}
                className="w-full h-auto"
                loading="eager"
              />
            </div>
          )}
          
          {/* Project description (if available) */}
          {project.description && (
            <div className="max-w-3xl mb-16">
              {descriptionParagraphs.map((paragraph, index) => (
                <p key={index} className="text-lg text-gray-700 font-light leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
              
              {/* Project info */}
              {project.projectInfo && (
                <div className="mt-10 pt-8 border-t border-gray-200">
                  <dl className="text-sm text-gray-500 space-y-2">
                    {project.projectInfo.projectName && (
                      <div className="flex">
                        <dt className="w-24 text-gray-400">Projekt</dt>
                        <dd>{project.projectInfo.projectName}</dd>
                      </div>
                    )}
                    {project.projectInfo.architect && (
                      <div className="flex">
                        <dt className="w-24 text-gray-400">Arkitekt</dt>
                        <dd>{project.projectInfo.architect}</dd>
                      </div>
                    )}
                    {project.projectInfo.role && (
                      <div className="flex">
                        <dt className="w-24 text-gray-400">Fryds rolle</dt>
                        <dd>{project.projectInfo.role}</dd>
                      </div>
                    )}
                    {project.projectInfo.client && (
                      <div className="flex">
                        <dt className="w-24 text-gray-400">Bygherre</dt>
                        <dd>{project.projectInfo.client}</dd>
                      </div>
                    )}
                    {project.projectInfo.location && (
                      <div className="flex">
                        <dt className="w-24 text-gray-400">Lokation</dt>
                        <dd>{project.projectInfo.location}</dd>
                      </div>
                    )}
                    {project.projectInfo.year && (
                      <div className="flex">
                        <dt className="w-24 text-gray-400">År</dt>
                        <dd>{project.projectInfo.year}</dd>
                      </div>
                    )}
                    {project.projectInfo.period && (
                      <div className="flex">
                        <dt className="w-24 text-gray-400">Periode</dt>
                        <dd>{project.projectInfo.period}</dd>
                      </div>
                    )}
                    {project.projectInfo.area && (
                      <div className="flex">
                        <dt className="w-24 text-gray-400">Areal</dt>
                        <dd>{project.projectInfo.area}</dd>
                      </div>
                    )}
                    {project.projectInfo.sourceUrl && project.projectInfo.sourceName && (
                      <div className="flex mt-4">
                        <dt className="w-24 text-gray-400">Kilde</dt>
                        <dd>
                          <a 
                            href={project.projectInfo.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-900 underline underline-offset-2 transition-colors"
                          >
                            {project.projectInfo.sourceName}
                          </a>
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </div>
          )}
          
          {/* Remaining images */}
          <div className="space-y-8 mb-20">
            {project.images.slice(1).map((image, index) => (
              <div key={index} className="w-full">
                <img
                  src={image}
                  alt={`${project.title} - billede ${index + 2}`}
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          
          {/* Project navigation */}
          <div className="border-t border-gray-200 pt-12 mb-20">
            <div className="flex justify-between items-center">
              {prevProject ? (
                <Link 
                  to={`/projekter/${prevProject.slug}`}
                  className="group flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-3 transition-transform group-hover:-translate-x-1" />
                  <div className="text-left">
                    <span className="text-xs tracking-wide uppercase text-gray-400 block">Forrige</span>
                    <span className="text-sm">{prevProject.title}</span>
                  </div>
                </Link>
              ) : (
                <div />
              )}
              
              {nextProject ? (
                <Link 
                  to={`/projekter/${nextProject.slug}`}
                  className="group flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                >
                  <div className="text-right">
                    <span className="text-xs tracking-wide uppercase text-gray-400 block">Næste</span>
                    <span className="text-sm">{nextProject.title}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 ml-3 transition-transform group-hover:translate-x-1" />
                </Link>
              ) : (
                <div />
              )}
            </div>
          </div>
          
          {/* Contact CTA */}
          <div className="border-t border-gray-200 pt-16">
            <div className="max-w-2xl">
              <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-4">
                Skal vi tale om dit projekt?
              </h2>
              <p className="text-gray-600 font-light leading-relaxed mb-8">
                Vi arbejder tæt sammen med både arkitekter og bygherrer om at realisere projekter med fokus på kvalitet og detaljer.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center space-x-3 text-gray-900 text-lg font-light border-b border-gray-900 pb-2 hover:text-gray-600 hover:border-gray-600 transition-all duration-300 group"
              >
                <span>Kontakt Fryd!</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
              </Link>
              <div className="mt-8 text-sm text-gray-400">
                <a 
                  href={`tel:${companyInfo.contact.phone.replace(/\s/g, '')}`}
                  className="hover:text-gray-600 transition-colors"
                >
                  {companyInfo.contact.phone}
                </a>
                <span className="mx-3">·</span>
                <a 
                  href={`mailto:${companyInfo.contact.email}`}
                  className="hover:text-gray-600 transition-colors"
                >
                  {companyInfo.contact.email}
                </a>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
